import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  RateReview as ReviewIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../i18n';
import { useNavigate } from 'react-router-dom';
import { peerReviewService, PeerReviewAssignment } from '../../services/peerReviewService';

const PeerReviewList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [pendingReviews, setPendingReviews] = useState<PeerReviewAssignment[]>([]);
  const [mySubmissions, setMySubmissions] = useState<PeerReviewAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [pending, submissions] = await Promise.all([
        peerReviewService.getPendingReviews(),
        peerReviewService.getMySubmissions(),
      ]);
      setPendingReviews(pending);
      setMySubmissions(submissions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'pending':
        return <Chip label={t('peerReview.status.pending', 'Ausstehend')} color="warning" size="small" />;
      case 'in_review':
        return <Chip label={t('peerReview.status.inReview', 'In Bearbeitung')} color="info" size="small" />;
      case 'completed':
        return <Chip label={t('peerReview.status.completed', 'Abgeschlossen')} color="success" size="small" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1f3240' }}>
        {t('peerReview.title', 'Peer Reviews')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab
          label={
            <Badge badgeContent={pendingReviews.length} color="error">
              {t('peerReview.tabs.toReview', 'Zu bewerten')}
            </Badge>
          }
        />
        <Tab label={t('peerReview.tabs.mySubmissions', 'Meine Abgaben (%{count})', { count: mySubmissions.length })} />
      </Tabs>

      <Paper>
        {tab === 0 ? (
          pendingReviews.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <ReviewIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography color="text.secondary">
                {t('peerReview.noPendingReviews', 'Keine ausstehenden Reviews')}
              </Typography>
            </Box>
          ) : (
            <List>
              {pendingReviews.map((assignment) => (
                <ListItem key={assignment.id} divider>
                  <ListItemText
                    primary={
                      <Box>
                        <Typography variant="body1">{assignment.lessonTitle}</Typography>
                        {assignment.courseTitle && (
                          <Typography variant="caption" color="text.secondary">{assignment.courseTitle}</Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                        {getStatusChip(assignment.status)}
                        <Typography variant="caption" color="text.secondary">
                          {t('peerReview.assignedOn', 'Zugewiesen am %{date}', {
                            date: new Date(assignment.assignedAt).toLocaleDateString('de-DE'),
                          })}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigate(`/peer-review/${assignment.id}`)}
                    >
                      {t('peerReview.review', 'Bewerten')}
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )
        ) : mySubmissions.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <AssignmentIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography color="text.secondary">
              {t('peerReview.noSubmissions', 'Keine Abgaben vorhanden')}
            </Typography>
          </Box>
        ) : (
          <List>
            {mySubmissions.map((assignment) => (
              <ListItem key={assignment.id} divider>
                <ListItemText
                  primary={
                    <Box>
                      <Typography variant="body1">{assignment.lessonTitle}</Typography>
                      {assignment.courseTitle && (
                        <Typography variant="caption" color="text.secondary">{assignment.courseTitle}</Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                      {getStatusChip(assignment.status)}
                      <Typography variant="caption" color="text.secondary">
                        {t('peerReview.submittedOn', 'Eingereicht am %{date}', {
                          date: new Date(assignment.assignedAt).toLocaleDateString('de-DE'),
                        })}
                      </Typography>
                      {assignment.reviews && assignment.reviews.length > 0 && (
                        <Chip
                          label={t('peerReview.reviewsReceived', '%{count} Reviews', { count: assignment.reviews.length })}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(`/peer-review/${assignment.id}/result`)}
                    disabled={!assignment.reviews || assignment.reviews.length === 0}
                  >
                    {t('peerReview.viewFeedback', 'Feedback ansehen')}
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default PeerReviewList;
